import { Component, EventEmitter, Input, Output } from '@angular/core';

import { TipoBuscaEnum } from '../../models/tipo-busca.enum';
import { ConsultaLancamentosOutput } from '../../models/consulta-lancamentos-output';

type Granularidade = 'dia' | 'mes' | 'ano';

interface ItemPizza {
  categoria: string;
  valor: number;
}

interface NivelPizza {
  data: any;
  outrosItens: ItemPizza[];
  outrosIndex: number;
  totalGeral: number;
}

interface ItemBarraSaldoPorCategoria {
  nome: string;
  receita: number;
  despesa: number;
}

interface NivelBarraSaldoPorCategoria {
  data: any;
  outrosItens: ItemBarraSaldoPorCategoria[];
  outrosIndex: number;
  totalGeral: number;
}

const formatBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatBRLCompacto = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' } as any);

class PizzaState {
  pilha: NivelPizza[] = [];
  exibir = true;

  get chartData(): any    { return this.pilha.at(-1)?.data ?? null; }
  get temOutros(): boolean { return (this.pilha.at(-1)?.outrosIndex ?? -1) >= 0; }
  get podeNavegar(): boolean { return this.pilha.length > 1; }

  voltar(): void { if (this.pilha.length > 1) this.pilha = this.pilha.slice(0, -1); }
  reset(): void  { if (this.pilha.length > 1) this.pilha = [this.pilha[0]]; }
}

class BarraSaldoPorCategoriaState {
  pilha: NivelBarraSaldoPorCategoria[] = [];
  exibir = true;

  get chartData(): any    { return this.pilha.at(-1)?.data ?? null; }
  get temOutros(): boolean { return (this.pilha.at(-1)?.outrosIndex ?? -1) >= 0; }
  get podeNavegar(): boolean { return this.pilha.length > 1; }
  get altura(): string {
    const qtdBarras = this.pilha.at(-1)?.data?.labels?.length ?? 0;
    return `${Math.max(240, qtdBarras * 36 + 60)}px`;
  }

  voltar(): void { if (this.pilha.length > 1) this.pilha = this.pilha.slice(0, -1); }
  reset(): void  { if (this.pilha.length > 1) this.pilha = [this.pilha[0]]; }
}

export interface DuploCliquePizzaEvent {
  tipoBusca: TipoBuscaEnum;
  id: number;
}

export interface DuploCliqueBarraEvent {
  dataInicio: string;
  dataFim: string;
}

@Component({
  selector: 'app-relatorio-graficos',
  templateUrl: './relatorio-graficos.component.html',
  styleUrl: './relatorio-graficos.component.scss',
  standalone: false,
})
export class RelatorioGraficosComponent {

  private _resultado!: ConsultaLancamentosOutput;

  @Output() duplocliquePizza = new EventEmitter<DuploCliquePizzaEvent>();
  @Output() duploCliqueBarra = new EventEmitter<DuploCliqueBarraEvent>();

  @Input() set resultado(value: ConsultaLancamentosOutput) {
    this._resultado = value;
    if (value) {
      this.construirGraficos();
    }
  }
  get resultado(): ConsultaLancamentosOutput { return this._resultado; }

  private readonly CORES_GRAFICOS = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2',
    '#59a14f', '#edc948', '#b07aa1', '#ff9da7',
    '#9c755f', '#bab0ac',
  ];

  private readonly LIMIAR_OUTROS = 0.05;
  private readonly MIN_PRINCIPAIS = 5;
  private readonly COR_OUTROS = '#cccccc';
  private corPorCategoria = new Map<string, string>();
  private corPorTipo = new Map<string, string>();
  private idPorCategoria = new Map<string, number>();
  private idPorTipo = new Map<string, number>();
  private chavesBarras: string[] = [];

  granularidade: Granularidade = 'mes';

  // Pizza — categoria (somente RelatorioCaixa)
  pizzaCatReceita  = new PizzaState();
  pizzaCatDespesa  = new PizzaState();

  // Pizza — tipo (todos exceto TipoLancamento)
  pizzaTipoReceita = new PizzaState();
  pizzaTipoDespesa = new PizzaState();

  // Barra de saldo por categoria/tipo (créditos e débitos) — mesmas regras de exibição das pizzas
  barraSaldoPorCategoria = new BarraSaldoPorCategoriaState();
  barraSaldoPorTipo = new BarraSaldoPorCategoriaState();

  chartBarrasMensal: any = null;
  chartVariacaoPercentual: any = null;
  chartBarrasSaldo: any = null;
  chartVariacaoPercentualSaldo: any = null;
  chartSaldoAcumulado: any = null;

  exibirBarrasMensal = true;
  exibirBarrasSaldo = true;
  exibirSaldoAcumulado = true;

  get exibirPizzaCategoria(): boolean { return this.pizzaCatReceita.exibir; }
  set exibirPizzaCategoria(v: boolean) { this.pizzaCatReceita.exibir = v; this.pizzaCatDespesa.exibir = v; }

  get exibirPizzaTipo(): boolean { return this.pizzaTipoReceita.exibir; }
  set exibirPizzaTipo(v: boolean) { this.pizzaTipoReceita.exibir = v; this.pizzaTipoDespesa.exibir = v; }

  get exibirGraficosCategoriaPizza(): boolean {
    const tipo = this._resultado?.parametrosBusca?.tipoBusca;
    return tipo === TipoBuscaEnum.RelatorioCaixa && (this._resultado?.totaisPorCategoria.length ?? 0) > 0;
  }

  get exibirGraficosTipoPizza(): boolean {
    const tipo = this._resultado?.parametrosBusca?.tipoBusca;
    return tipo !== TipoBuscaEnum.TipoLancamento && (this._resultado?.lancamentos.length ?? 0) > 0;
  }

  readonly chartOptionsPizza: any = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          generateLabels: (chart: any) => {
            const labels: string[] = chart.data.labels ?? [];
            const bgColors: string[] = chart.data.datasets[0]?.backgroundColor ?? [];
            return labels.map((label, i) => ({
              text: label.length > 20 ? label.substring(0, 20) + '…' : label,
              fillStyle: bgColors[i] ?? '#ccc',
              strokeStyle: bgColors[i] ?? '#ccc',
              lineWidth: 0,
              hidden: false,
              index: i,
              datasetIndex: 0,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const valor = ctx.raw as number;
            const total = ctx.dataset.totalGeral as number;
            const pct = total > 0
              ? (valor / total * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '0,0';
            return ` ${formatBRL(valor)}  (${pct}%)`;
          },
        },
      },
    },
    onHover: (event: any, elements: any[]) => {
      if (event?.native?.target) {
        event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
      }
    },
  };

  readonly chartOptionsBarra: any = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatBRL(ctx.raw as number)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v: any) => formatBRLCompacto(v as number),
        },
      },
    },
  };

  readonly chartOptionsBarraSaldo: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatBRL(ctx.raw as number)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          callback: (v: any) => formatBRLCompacto(v as number),
        },
      },
    },
    onHover: (event: any, elements: any[]) => {
      if (event?.native?.target) {
        event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
      }
    },
  };

  readonly chartOptionsLinha = this.chartOptionsBarra;

  readonly chartOptionsPercentual: any = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const valor = ctx.raw as number;
            return ` ${ctx.dataset.label}: ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (v: any) => `${v}%` },
      },
    },
  };

  get labelGranularidade(): string {
    return this.granularidade === 'dia' ? 'por Dia'
      : this.granularidade === 'ano' ? 'por Ano'
      : 'por Mês';
  }

  public onClickPizzaCatReceita(event: any): void  { this.drillDown(this.pizzaCatReceita,  this.corPorCategoria, event); }
  public onClickPizzaCatDespesa(event: any): void  { this.drillDown(this.pizzaCatDespesa,  this.corPorCategoria, event); }
  public onClickPizzaTipoReceita(event: any): void { this.drillDown(this.pizzaTipoReceita, this.corPorTipo, event); }
  public onClickPizzaTipoDespesa(event: any): void { this.drillDown(this.pizzaTipoDespesa, this.corPorTipo, event); }

  private drillDown(pizza: PizzaState, corMap: Map<string, string>, event: any): void {
    const index = event?.element?.index ?? event?.element?._index;
    if (index == null) return;
    const nivel = pizza.pilha.at(-1)!;
    if (index === nivel.outrosIndex) {
      pizza.pilha = [...pizza.pilha, this.construirNivelPizza(nivel.outrosItens, corMap, nivel.totalGeral)];
    }
  }

  public onClickBarraSaldoPorCategoria(event: MouseEvent, pChartRef: any): void { this.drillDownBarraSaldo(this.barraSaldoPorCategoria, event, pChartRef); }
  public onClickBarraSaldoPorTipo(event: MouseEvent, pChartRef: any): void      { this.drillDownBarraSaldo(this.barraSaldoPorTipo, event, pChartRef); }

  private drillDownBarraSaldo(estado: BarraSaldoPorCategoriaState, event: MouseEvent, pChartRef: any): void {
    const chart = pChartRef?.chart;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(event as unknown as Event, 'nearest', { intersect: false, axis: 'y' }, false);
    if (!elements.length) return;
    const index = elements[0].index;
    const nivel = estado.pilha.at(-1)!;
    if (index === nivel.outrosIndex) {
      estado.pilha = [...estado.pilha, this.construirNivelBarraSaldo(nivel.outrosItens, nivel.totalGeral)];
    }
  }

  private calcularGranularidade(): Granularidade {
    const mesesDistintos = new Set(
      this._resultado.lancamentos.map(l => {
        const d = new Date(l.data);
        return `${d.getFullYear()}-${d.getMonth()}`;
      })
    ).size;

    if (mesesDistintos <= 1) return 'dia';
    if (mesesDistintos <= 24) return 'mes';
    return 'ano';
  }

  private chaveAgrupamento(data: Date, granularidade: Granularidade): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');

    if (granularidade === 'dia') return `${ano}-${mes}-${dia}`;
    if (granularidade === 'ano') return `${ano}`;
    return `${ano}-${mes}`;
  }

  private labelPeriodo(chave: string, granularidade: Granularidade): string {
    if (granularidade === 'dia') {
      const [ano, mes, dia] = chave.split('-');
      return new Date(Number(ano), Number(mes) - 1, Number(dia))
        .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    }
    if (granularidade === 'ano') {
      return chave;
    }
    const [ano, mes] = chave.split('-');
    return new Date(Number(ano), Number(mes) - 1)
      .toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
  }

  private construirGraficos(): void {
    this.granularidade = this.calcularGranularidade();
    this.construirGraficosPizzaCategoria();
    this.construirGraficosPizzaTipo();
    this.construirGraficoBarraSaldoPorCategoria();
    this.construirGraficoBarraSaldoPorTipo();
    this.construirGraficoMensal();
    this.construirGraficoSaldoMensal();
    this.construirGraficoSaldoAcumulado();
  }

  private construirGraficosPizzaCategoria(): void {
    if (this._resultado.parametrosBusca?.tipoBusca !== TipoBuscaEnum.RelatorioCaixa) {
      this.pizzaCatReceita.pilha = [];
      this.pizzaCatDespesa.pilha = [];
      return;
    }

    const totais = this._resultado.totaisPorCategoria;

    // Cor fixada por categoria para consistência entre receita e despesa
    this.corPorCategoria = new Map(totais.map((t, i) => [t.categoria, this.CORES_GRAFICOS[i % this.CORES_GRAFICOS.length]]));
    this.idPorCategoria  = new Map(totais.filter(t => t.categoriaId).map(t => [t.categoria, t.categoriaId!]));

    const itens = totais.map(t => ({ nome: t.categoria, receita: t.totalReceita, despesa: t.totalDespesa }));
    [this.pizzaCatReceita.pilha, this.pizzaCatDespesa.pilha] = this.iniciarPilhasPizza(itens, this.corPorCategoria);
  }

  private construirGraficosPizzaTipo(): void {
    const tipoBusca = this._resultado.parametrosBusca?.tipoBusca;

    if (tipoBusca === TipoBuscaEnum.TipoLancamento) {
      this.pizzaTipoReceita.pilha = [];
      this.pizzaTipoDespesa.pilha = [];
      return;
    }

    let ordenados: { nome: string; receita: number; despesa: number }[];

    if (tipoBusca === TipoBuscaEnum.RelatorioCaixa) {
      // RelatorioCaixa: totaisPorCategoria agrupa por categoria, não por tipo → agrupa lancamentos manualmente
      const porTipo = new Map<string, { receita: number; despesa: number }>();
      for (const l of this._resultado.lancamentos) {
        const nome = l.tipoLancamento.nome;
        if (!porTipo.has(nome)) porTipo.set(nome, { receita: 0, despesa: 0 });
        const t = porTipo.get(nome)!;
        if (l.valor > 0) t.receita += l.valor;
        else t.despesa += l.valor;
      }
      ordenados = Array.from(porTipo.entries())
        .map(([nome, v]) => ({ nome, receita: v.receita, despesa: v.despesa }))
        .sort((a, b) => Math.abs(b.receita + b.despesa) - Math.abs(a.receita + a.despesa));
    } else {
      // Conta e CategoriaRelatorio: totaisPorCategoria já agrupa por tipo e está ordenado por |saldo| desc
      ordenados = this._resultado.totaisPorCategoria
        .map(t => ({ nome: t.categoria, receita: t.totalReceita, despesa: t.totalDespesa }));
    }

    // Cor fixada por tipo para consistência entre receita e despesa
    this.corPorTipo  = new Map(ordenados.map((t, i) => [t.nome, this.CORES_GRAFICOS[i % this.CORES_GRAFICOS.length]]));
    this.idPorTipo   = new Map(this._resultado.lancamentos.map(l => [l.tipoLancamento.nome, l.tipoLancamento.id]));

    [this.pizzaTipoReceita.pilha, this.pizzaTipoDespesa.pilha] = this.iniciarPilhasPizza(ordenados, this.corPorTipo);
  }

  private iniciarPilhasPizza(
    itens: { nome: string; receita: number; despesa: number }[],
    corMap: Map<string, string>
  ): [NivelPizza[], NivelPizza[]] {
    const comReceita = itens.filter(t => t.receita > 0).map(t => ({ categoria: t.nome, valor: t.receita }));
    const comDespesa = itens.filter(t => t.despesa < 0).map(t => ({ categoria: t.nome, valor: Math.abs(t.despesa) }));
    return [
      comReceita.length > 0 ? [this.construirNivelPizza(comReceita, corMap)] : [],
      comDespesa.length > 0 ? [this.construirNivelPizza(comDespesa, corMap)] : [],
    ];
  }

  private separarOutros<T>(itens: T[], valorFn: (i: T) => number, total: number): { principais: T[]; outros: T[] } {
    if (total <= 0) return { principais: itens, outros: [] };

    // Garante um mínimo de fatias (os maiores itens), e a partir daí só continua
    // incluindo como principal enquanto o próximo item ainda bater no limiar de 5%.
    const ordenados = [...itens].sort((a, b) => valorFn(b) - valorFn(a));
    let qtdPrincipais = Math.min(this.MIN_PRINCIPAIS, ordenados.length);
    while (qtdPrincipais < ordenados.length && valorFn(ordenados[qtdPrincipais]) / total >= this.LIMIAR_OUTROS) {
      qtdPrincipais++;
    }

    const incluidos = new Set(ordenados.slice(0, qtdPrincipais));
    let principais = itens.filter(i => incluidos.has(i));
    let outros = itens.filter(i => !incluidos.has(i));

    if (outros.length === 1) {
      principais = itens;
      outros = [];
    }

    return { principais, outros };
  }

  private construirNivelPizza(itens: ItemPizza[], corMap: Map<string, string>, totalGeral?: number): NivelPizza {
    const total = itens.reduce((s, i) => s + i.valor, 0);
    const tg = totalGeral ?? total;
    const { principais, outros } = this.separarOutros(itens, i => i.valor, total);

    const labels: string[] = principais.map(i => i.categoria);
    const data: number[] = principais.map(i => i.valor);
    const backgroundColor: string[] = principais.map(i => corMap.get(i.categoria) ?? '#ccc');

    let outrosIndex = -1;
    if (outros.length > 0) {
      outrosIndex = labels.length;
      labels.push(`Outros (${outros.length})`);
      data.push(outros.reduce((s, i) => s + i.valor, 0));
      backgroundColor.push(this.COR_OUTROS);
    }

    return {
      data: { labels, datasets: [{ data, backgroundColor, totalGeral: tg }] },
      outrosItens: outros,
      outrosIndex,
      totalGeral: tg,
    };
  }

  private construirGraficoBarraSaldoPorCategoria(): void {
    if (this._resultado.parametrosBusca?.tipoBusca !== TipoBuscaEnum.RelatorioCaixa) {
      this.barraSaldoPorCategoria.pilha = [];
      return;
    }

    const itens = this._resultado.totaisPorCategoria
      .map(t => ({ nome: t.categoria, receita: t.totalReceita, despesa: t.totalDespesa }))
      .filter(i => i.receita !== 0 || i.despesa !== 0);

    this.barraSaldoPorCategoria.pilha = itens.length > 0 ? [this.construirNivelBarraSaldo(itens)] : [];
  }

  private construirGraficoBarraSaldoPorTipo(): void {
    const tipoBusca = this._resultado.parametrosBusca?.tipoBusca;

    if (tipoBusca === TipoBuscaEnum.TipoLancamento) {
      this.barraSaldoPorTipo.pilha = [];
      return;
    }

    let itens: ItemBarraSaldoPorCategoria[];

    if (tipoBusca === TipoBuscaEnum.RelatorioCaixa) {
      // RelatorioCaixa: totaisPorCategoria agrupa por categoria, não por tipo → agrupa lancamentos manualmente
      const porTipo = new Map<string, { receita: number; despesa: number }>();
      for (const l of this._resultado.lancamentos) {
        const nome = l.tipoLancamento.nome;
        if (!porTipo.has(nome)) porTipo.set(nome, { receita: 0, despesa: 0 });
        const t = porTipo.get(nome)!;
        if (l.valor > 0) t.receita += l.valor;
        else t.despesa += l.valor;
      }
      itens = Array.from(porTipo.entries()).map(([nome, v]) => ({ nome, receita: v.receita, despesa: v.despesa }));
    } else {
      // Conta e CategoriaRelatorio: totaisPorCategoria já agrupa por tipo
      itens = this._resultado.totaisPorCategoria
        .map(t => ({ nome: t.categoria, receita: t.totalReceita, despesa: t.totalDespesa }));
    }

    itens = itens.filter(i => i.receita !== 0 || i.despesa !== 0);

    this.barraSaldoPorTipo.pilha = itens.length > 0 ? [this.construirNivelBarraSaldo(itens)] : [];
  }

  private construirNivelBarraSaldo(itens: ItemBarraSaldoPorCategoria[], totalGeral?: number): NivelBarraSaldoPorCategoria {
    const saldo = (i: ItemBarraSaldoPorCategoria) => i.receita + i.despesa;
    const total = itens.reduce((s, i) => s + Math.abs(saldo(i)), 0);
    const tg = totalGeral ?? total;

    const ordenados = [...itens].sort((a, b) => Math.abs(saldo(b)) - Math.abs(saldo(a)));
    const { principais, outros } = this.separarOutros(ordenados, i => Math.abs(saldo(i)), total);

    const labels: string[] = principais.map(i => i.nome);
    const saldos: number[] = principais.map(saldo);

    let outrosIndex = -1;
    if (outros.length > 0) {
      outrosIndex = labels.length;
      labels.push(`Outros (${outros.length})`);
      saldos.push(outros.reduce((s, i) => s + saldo(i), 0));
    }

    return {
      data: {
        labels,
        datasets: [{
          label: 'Saldo',
          data: saldos,
          backgroundColor: saldos.map(s => s >= 0 ? '#66BB6A' : '#EF5350'),
        }],
      },
      outrosItens: outros,
      outrosIndex,
      totalGeral: tg,
    };
  }

  private construirGraficoMensal(): void {
    const porPeriodo = new Map<string, { receita: number; despesa: number }>();

    for (const l of this._resultado.lancamentos) {
      const data = new Date(l.data);
      const chave = this.chaveAgrupamento(data, this.granularidade);

      if (!porPeriodo.has(chave)) {
        porPeriodo.set(chave, { receita: 0, despesa: 0 });
      }

      const periodo = porPeriodo.get(chave)!;
      if (l.valor > 0) {
        periodo.receita += l.valor;
      } else {
        periodo.despesa += l.valor;
      }
    }

    const periodosOrdenados = Array.from(porPeriodo.entries()).sort(([a], [b]) => a.localeCompare(b));

    this.chavesBarras = periodosOrdenados.map(([chave]) => chave);

    const labels: string[] = [];
    const receitas: number[] = [];
    const despesas: number[] = [];

    for (const [chave, valores] of periodosOrdenados) {
      labels.push(this.labelPeriodo(chave, this.granularidade));
      receitas.push(valores.receita);
      despesas.push(Math.abs(valores.despesa));
    }

    this.chartBarrasMensal = {
      labels,
      datasets: [
        { label: 'Receita', data: receitas, backgroundColor: '#66BB6A' },
        { label: 'Despesa', data: despesas, backgroundColor: '#EF5350' },
      ],
    };

    this.chartVariacaoPercentual = {
      labels,
      datasets: [
        { label: 'Variação Receita', data: this.calcularVariacaoPercentual(receitas), backgroundColor: '#66BB6A' },
        { label: 'Variação Despesa', data: this.calcularVariacaoPercentual(despesas), backgroundColor: '#EF5350' },
      ],
    };
  }

  private calcularVariacaoPercentual(valores: number[]): number[] {
    return valores.map((atual, i) => {
      if (i === 0) return 0;
      const anterior = valores[i - 1];
      if (anterior === 0) return 0;
      return (atual - anterior) / Math.abs(anterior) * 100;
    });
  }

  private construirGraficoSaldoMensal(): void {
    const porPeriodo = new Map<string, number>();

    for (const l of this._resultado.lancamentos) {
      const data = new Date(l.data);
      const chave = this.chaveAgrupamento(data, this.granularidade);
      porPeriodo.set(chave, (porPeriodo.get(chave) ?? 0) + l.valor);
    }

    const periodosOrdenados = Array.from(porPeriodo.entries()).sort(([a], [b]) => a.localeCompare(b));

    const labels: string[] = [];
    const saldos: number[] = [];

    for (const [chave, saldo] of periodosOrdenados) {
      labels.push(this.labelPeriodo(chave, this.granularidade));
      saldos.push(saldo);
    }

    this.chartBarrasSaldo = {
      labels,
      datasets: [{
        label: 'Saldo',
        data: saldos,
        backgroundColor: saldos.map(s => s >= 0 ? '#66BB6A' : '#EF5350'),
      }],
    };

    const variacaoSaldos = this.calcularVariacaoPercentual(saldos);
    this.chartVariacaoPercentualSaldo = {
      labels,
      datasets: [{
        label: 'Variação Saldo',
        data: variacaoSaldos,
        backgroundColor: variacaoSaldos.map(v => v >= 0 ? '#66BB6A' : '#EF5350'),
      }],
    };
  }

  private construirGraficoSaldoAcumulado(): void {
    const ordenados = [...this._resultado.lancamentos]
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());

    const porData = new Map<string, number>();
    let saldoAcumulado = 0;

    for (const l of ordenados) {
      saldoAcumulado += l.valor;
      const chave = new Date(l.data).toLocaleDateString('pt-BR');
      porData.set(chave, saldoAcumulado);
    }

    const labels: string[] = [];
    const dados: number[] = [];

    for (const [data, saldo] of porData.entries()) {
      labels.push(data);
      dados.push(saldo);
    }

    this.chartSaldoAcumulado = {
      labels,
      datasets: [{
        label: 'Saldo Acumulado',
        data: dados,
        borderColor: '#4e79a7',
        backgroundColor: 'rgba(78, 121, 167, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: porData.size > 60 ? 0 : 3,
      }],
    };
  }

  public buscarBarraChart(event: MouseEvent, pChartRef: any): void {
    const chart = pChartRef?.chart;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(event as unknown as Event, 'nearest', { intersect: false }, false);
    if (!elements.length) return;
    const chave = this.chavesBarras[elements[0].index];
    if (!chave) return;
    this.duploCliqueBarra.emit(this.chaveParaIntervalo(chave));
  }

  private chaveParaIntervalo(chave: string): DuploCliqueBarraEvent {
    if (this.granularidade === 'dia') {
      return { dataInicio: chave, dataFim: chave };
    }
    if (this.granularidade === 'mes') {
      const [ano, mes] = chave.split('-').map(Number);
      const ultimoDia = new Date(ano, mes, 0).getDate();
      return { dataInicio: `${chave}-01`, dataFim: `${chave}-${String(ultimoDia).padStart(2, '0')}` };
    }
    return { dataInicio: `${chave}-01-01`, dataFim: `${chave}-12-31` };
  }

  public buscarCategoriaChart(event: MouseEvent, pChartRef: any, chartEhDeTipo: boolean): void {
    const chart = pChartRef?.chart;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(event as unknown as Event, 'nearest', { intersect: true }, false);
    if (!elements.length) return;
    const label = (chart.data.labels as string[])[elements[0].index];
    if (!label || label.startsWith('Outros')) return;
    const tipoBusca = chartEhDeTipo ? TipoBuscaEnum.TipoLancamento : TipoBuscaEnum.CategoriaRelatorio;
    const id = chartEhDeTipo ? this.idPorTipo.get(label) : this.idPorCategoria.get(label);
    if (id == null) return;
    this.duplocliquePizza.emit({ tipoBusca, id });
  }

  public buscarCategoriaBarraSaldoChart(event: MouseEvent, pChartRef: any, chartEhDeTipo: boolean): void {
    const chart = pChartRef?.chart;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(event as unknown as Event, 'nearest', { intersect: false, axis: 'y' }, false);
    if (!elements.length) return;
    const label = (chart.data.labels as string[])[elements[0].index];
    if (!label || label.startsWith('Outros')) return;
    const tipoBusca = chartEhDeTipo ? TipoBuscaEnum.TipoLancamento : TipoBuscaEnum.CategoriaRelatorio;
    const id = chartEhDeTipo ? this.idPorTipo.get(label) : this.idPorCategoria.get(label);
    if (id == null) return;
    this.duplocliquePizza.emit({ tipoBusca, id });
  }
}
